import type { SampleConfigurationTypeKey } from '../../Generated/SampleConfigurationType.ts'
import type { TargetTaxonomyKey } from '../../Generated/TargetTaxonomy.ts'
import { cleanPrimer } from './iupac.ts'
import type { MultiplexVerdict } from './multiplexDetection.ts'
import { detectMultiplexing } from './multiplexDetection.ts'
import { identifyPrimerPairs } from './primerPairIdentification.ts'
import type { KnownPrimerPair } from './primerRegistry.ts'
import { asPrimerPair, findKnownPair, genePrimersFor } from './primerRegistry.ts'
import type { PrimerPair, PrimerTrimVerdict } from './primerTrimDetection.ts'
import { DEFAULT_PRIMER_TRIM_OPTIONS, detectPrimerTrimming } from './primerTrimDetection.ts'
import type { ReadLayout, ReadLayoutFile } from './readLayoutDetection.ts'
import { detectReadLayout } from './readLayoutDetection.ts'

/**
 * `high`: primers were entered and every signal agrees. `medium`: the primers were found in the registry,
 * or the layout rests on file names alone. `low`: no known pair is on the reads, so "trimmed" is an
 * assumption — an unregistered primer looks the same — and the plan asks rather than acts on it.
 */
export type DetectionConfidence = 'high' | 'medium' | 'low'

export type SampleConfigurationGuess = {
  configuration: SampleConfigurationTypeKey | 'UNKNOWN'
  confidence: DetectionConfidence | null
  layout: ReadLayout
  trimming: PrimerTrimVerdict
  multiplexing: MultiplexVerdict
  primerSource: 'entered' | 'registered' | 'none'
  /** The known pairs found across the judged samples; more than one means a mixed-primer batch. */
  pairsFound: KnownPrimerPair[]
  /** Without a found pair: the pairs sharing the one primer the reads show, which they cannot tell apart. */
  oneSidedPairs: KnownPrimerPair[]
  /** Files no registered pair is identified on, but the nearest one opens partly: primers on, likely an unregistered variant. */
  nearPairFiles?: number
  judgedFiles?: number
  reason: string
}

export type ClassifierOptions = {
  /** Applies to every file that does not carry its own `primers`. */
  primers?: PrimerPair | null
  /** From {@link normalizePrimerRegistry}. */
  registry?: readonly KnownPrimerPair[]
  /** Puts the pairs registered for it on the shortlist. */
  target?: TargetTaxonomyKey | null
}

/** Files carrying the per-sample primers a batch may declare; `primers` absent means "not entered". */
export type ClassifiableFile = ReadLayoutFile & { primers?: PrimerPair | null }

/** The primer searches cost milliseconds a file; a handful of files settles a batch. */
const FILES_JUDGED = 6
/** A negative control or a near-empty file may disagree; two thirds of the decided files must not. */
const MAJORITY = 2 / 3

const majority = <V extends string>(verdicts: readonly V[], unknown: V): V => {
  const decided = verdicts.filter((v) => v !== unknown)
  const counts = new Map<V, number>()
  for (const v of decided) {
    counts.set(v, (counts.get(v) ?? 0) + 1)
  }
  for (const [v, n] of counts) {
    if (n / decided.length >= MAJORITY) {
      return v
    }
  }
  return unknown
}

const entered = (primers?: PrimerPair | null): PrimerPair | null =>
  primers && cleanPrimer(primers.forward) && cleanPrimer(primers.reverse) ? primers : null

type FileEvidence = {
  primers: PrimerPair | null
  source: 'entered' | 'registered' | 'none'
  pair: KnownPrimerPair | null
  nearest: KnownPrimerPair | null
  oneSided?: KnownPrimerPair[]
}

/**
 * Up to {@link FILES_JUDGED} files grouped by sample — mates together, since an R2 file alone cannot tell
 * apart the pairs that share its primer.
 */
const sampleGroups = (files: readonly ClassifiableFile[], pairs: readonly [number, number][]): ClassifiableFile[][] => {
  const hasReads = (file: ClassifiableFile) => (file.reads?.length ?? 0) > 0
  const mates = pairs.map(([a, b]) => [files[a], files[b]].filter(hasReads)).filter((group) => group.length > 0)
  const grouped = new Set(pairs.flat())
  const singles = files.filter((file, i) => !grouped.has(i) && hasReads(file)).map((file) => [file])
  const groups: ClassifiableFile[][] = []
  let count = 0
  for (const group of [...mates, ...singles]) {
    if (count >= FILES_JUDGED) {
      break
    }
    groups.push(group)
    count += group.length
  }
  return groups
}

/**
 * Each sample gets its own pair: the one entered for it (or for the batch), else the one its reads carry.
 * A sample showing only a primer several pairs share is judged on the first of them — the shared primer
 * is what the trimming and tag checks see — but names no pair.
 */
const resolvePrimers = (
  groups: readonly ClassifiableFile[][],
  options: ClassifierOptions,
  oneMate: (group: readonly ClassifiableFile[]) => boolean
): FileEvidence[] => {
  const registry = options.registry ?? []
  const given = groups.map((group) => entered(group[0].primers) ?? entered(options.primers))
  const shortlist = registry.filter((pair) => !!options.target && pair.amplicons.includes(options.target))
  const searched = groups.filter((_, i) => !given[i])
  const matches = identifyPrimerPairs(
    searched.map((group) => group.flatMap((file) => file.reads ?? [])),
    registry,
    shortlist,
    DEFAULT_PRIMER_TRIM_OPTIONS,
    (i) => oneMate(searched[i])
  )
  let next = 0
  return groups.map((_, i): FileEvidence => {
    const primers = given[i]
    if (primers) {
      return { primers: genePrimersFor(primers, registry), source: 'entered', pair: findKnownPair(primers, registry), nearest: null }
    }
    const { match, nearest, oneSided } = matches[next++]
    if (match) {
      return { primers: asPrimerPair(match.pair), source: 'registered', pair: match.pair, nearest: null }
    }
    return oneSided
      ? { primers: asPrimerPair(oneSided[0]), source: 'registered', pair: null, nearest: null, oneSided }
      : { primers: null, source: 'none', pair: null, nearest }
  })
}

/**
 * The full sample configuration one dropped batch points at: paired or merged from
 * {@link detectReadLayout}, primers on or off from {@link detectPrimerTrimming}, and — for untrimmed
 * pairs — whether each file pair holds many inline-tagged samples ({@link detectMultiplexing}).
 * Every file is judged against its own primer pair.
 */
export const classifySampleConfiguration = (
  files: readonly ClassifiableFile[],
  options: ClassifierOptions = {}
): SampleConfigurationGuess => {
  const layoutResult = detectReadLayout(files)
  const layout = layoutResult.layout
  const groups = sampleGroups(files, layoutResult.pairs)
  /* raw mates without their partner end before the partner primer: an R1 never shows the reverse */
  const rawMates = layout === 'PAIRED' || !!layoutResult.unmated?.rawLength
  const groupEvidence = resolvePrimers(groups, options, (group) => rawMates && group.length === 1)
  const judged = groups.flat()
  const evidence = groups.flatMap((group, i) => group.map(() => groupEvidence[i]))
  /* raw mates always open with their primer, so their 3′ ends — read-through into the partner primer
     that 5′-only trimming leaves behind — are not evidence; merged reads need both ends. Files of an
     unknown layout count as raw mates, so a single-end answer knows their trimming. */
  const trimOptions = { ...DEFAULT_PRIMER_TRIM_OPTIONS, evidence: layout === 'MERGED_OR_SINGLE' ? ('read' as const) : ('start' as const) }
  const sources = groupEvidence.map((e) => e.source)
  const primerSource = sources.includes('entered') ? 'entered' : sources.includes('registered') ? 'registered' : 'none'
  const pairsFound = [...new Map(groupEvidence.flatMap((e) => (e.pair ? [[e.pair.id, e.pair] as const] : []))).values()]
  const oneSidedPairs = [...new Map(groupEvidence.flatMap((e) => (e.oneSided ?? []).map((pair) => [pair.id, pair] as const))).values()]
  /* a file no known pair is on votes trimmed — an unregistered primer would look the same, hence low
     confidence; one the nearest pair opens partly is neither trimmed nor identified */
  const verdicts = judged.map((file, i): PrimerTrimVerdict => {
    const { primers, nearest } = evidence[i]
    if (primers) {
      return detectPrimerTrimming(file.reads ?? [], primers, trimOptions).verdict
    }
    return nearest && detectPrimerTrimming(file.reads ?? [], asPrimerPair(nearest), trimOptions).verdict !== 'TRIMMED'
      ? 'UNKNOWN'
      : 'TRIMMED'
  })
  /* only the nearest pair yields UNKNOWN for a file without identified primers */
  const nearPairFiles = verdicts.filter((verdict, i) => !evidence[i].primers && verdict === 'UNKNOWN').length
  const base = { layout, primerSource, pairsFound, oneSidedPairs, nearPairFiles, judgedFiles: judged.length } as const
  const unknown = (
    reason: string,
    trimming: PrimerTrimVerdict = 'UNKNOWN',
    multiplexing: MultiplexVerdict = 'UNKNOWN'
  ): SampleConfigurationGuess => ({
    ...base,
    configuration: 'UNKNOWN',
    confidence: null,
    trimming,
    multiplexing,
    reason
  })

  if (judged.length === 0) {
    return unknown(layout === 'UNKNOWN' ? `layout: ${layoutResult.reason}` : 'no reads to look for primers in')
  }

  const trimming = majority(verdicts, 'UNKNOWN')
  if (layout === 'UNKNOWN') {
    return unknown(`layout: ${layoutResult.reason}`, trimming)
  }
  if (trimming === 'UNKNOWN') {
    return unknown('primers neither clearly on nor clearly off')
  }
  const assumed = evidence.filter((e, i) => !e.primers && verdicts[i] === trimming).length
  const decided = verdicts.filter((v) => v !== 'UNKNOWN').length
  const confidence: DetectionConfidence =
    trimming === 'TRIMMED' && assumed * 2 > decided
      ? 'low'
      : primerSource === 'entered' && layoutResult.confidence === 'high'
        ? 'high'
        : 'medium'
  const primerNote =
    confidence === 'low'
      ? 'no known primer pair on the reads (trimmed, or an unregistered primer)'
      : `primers ${trimming === 'TRIMMED' ? 'off' : 'on'}`

  if (layout === 'MERGED_OR_SINGLE') {
    return {
      ...base,
      configuration: trimming === 'TRIMMED' ? 'MERGED_AND_TRIMMED_SAMPLES' : 'MERGED_SAMPLES',
      confidence,
      trimming,
      multiplexing: 'UNKNOWN',
      reason: `${layoutResult.reason}; ${primerNote}`
    }
  }
  /* demultiplexing cuts on the tag in front of the primer, so a trimmed pair cannot be a multiplexed one */
  if (trimming === 'TRIMMED') {
    return {
      ...base,
      configuration: 'TRIMMED_SAMPLES',
      confidence,
      trimming,
      multiplexing: confidence === 'low' ? 'UNKNOWN' : 'SINGLE_SAMPLE',
      reason: `pairs, ${primerNote}`
    }
  }
  const multiplexing = majority(
    judged.flatMap((file, i) => {
      const primers = evidence[i].primers
      return primers ? [detectMultiplexing(file.reads ?? [], primers).verdict] : []
    }),
    'UNKNOWN'
  )
  if (multiplexing === 'UNKNOWN') {
    return unknown('pairs with primers on, but one sample per file vs many is unclear', trimming, multiplexing)
  }
  return {
    ...base,
    configuration: multiplexing === 'MULTIPLEXED' ? 'COMBINED_SAMPLE_PAIRS' : 'SINGLE_SAMPLE_PAIRS',
    confidence,
    trimming,
    multiplexing,
    reason: `pairs, primers on, ${multiplexing === 'MULTIPLEXED' ? 'many inline tags per file' : 'one sample per file'}`
  }
}
